import React, { useState } from 'react';
import { NSButton, NSCard } from 'bricks';
import {
    CardBody, Col, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown,
} from 'reactstrap';
import NSUserAvatar from 'bricks/NSUserAvatar/NSUserAvatar';
import { faFileLines, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { formatUTCtoLocal } from 'ns_libs/formatter';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import useToast from 'hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import useDeleteCRMActivity from './hooks/useDeleteCRMActivity';
import { ACTIVITY_TYPE } from '../constants';
import getCRMDealActivitiesInfiniteQueryOptions from './hooks/useGetCRMDealActivities';

export interface IActivityCardProps {
    id: number;
    activityType: typeof ACTIVITY_TYPE [keyof typeof ACTIVITY_TYPE] | null;
    header: React.ReactNode;
    activityDate: string;
    content: React.ReactNode;
    icon: IconDefinition | undefined;
    color: string;
    attachment?: never // not implemented
    organizationId: string;
    dealId: number;
}

const ActivityCard = ({
    id, header, activityDate, content, icon, color, attachment, activityType, organizationId, dealId,
}: IActivityCardProps) => {
    const [isMenuToggleVisible, setIsMenuToggleVisible] = useState(true);

    const {
        mutate: deleteActivity,
        status: deleteStatus,
    } = useDeleteCRMActivity();
    const queryClient = useQueryClient();

    const { showError } = useToast();

    const handleDelete = () => deleteActivity({
        organizationId,
        activityId: id,
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(getCRMDealActivitiesInfiniteQueryOptions(organizationId, dealId));
        },
        onError: error => {
            showError(`There was an error deleting the ${activityType?.toLowerCase() || 'activity'}. Please try again. ${error?.message}`);
        },
    });

    if (deleteStatus === 'pending' || deleteStatus === 'success') {
        return null;
    }

    return (
        <NSCard
            className="NSCard--level-4 p-2 mb-2"
        >
            <CardBody
                onMouseEnter={() => setIsMenuToggleVisible(true)}
                onMouseLeave={() => setIsMenuToggleVisible(false)}
                className="d-flex justify-content-between align-items-top p-0"
            >
                <NSUserAvatar
                    size={40}
                    radius={20}
                    shouldUseIcon
                    className="mr-2"
                    textColor="white"
                    backgroundColor={color}
                    customIcon={icon}
                />
                <div className="flex-grow-1 d-flex justify-content-between w-100 flex-column">
                    <div className="d-flex justify-content-between align-items-top w-100">
                        {header}
                        <div className="d-flex align-items-center">
                            <div className="text-muted">
                                {formatUTCtoLocal(activityDate)}
                            </div>
                            { isMenuToggleVisible && (
                                <UncontrolledDropdown>
                                    <DropdownToggle
                                        tag="div"
                                        className="cursor--pointer px-2 ml-1"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisVertical} />
                                    </DropdownToggle>
                                    <DropdownMenu right>
                                        <DropdownItem onClick={handleDelete} className="heap-deal-activity-delete-note">
                                            Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            )}
                        </div>
                    </div>

                    <div>
                        <p className="mb-0 text-white">
                            {content}
                        </p>
                    </div>
                    {attachment && (
                        <Row className="mx-0 my-1">
                            <Col xs="4" className="p-0">
                                <div className="DealActivity__attachment NSCard--level-5 rounded">
                                    <div className="p-2 d-flex align-items-center justify-content-between">
                                        <div>
                                            <FontAwesomeIcon icon={faFileLines} className="text-muted mr-2" />
                                            <span className="text-white">{attachment}</span>
                                        </div>
                                        <NSButton color="link" className="ml-auto p-0">
                                            <FontAwesomeIcon color="white" icon={faDownload} />
                                        </NSButton>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                </div>

            </CardBody>
        </NSCard>
    );
};
export default ActivityCard;
